/**
 * Centralized Theme Configuration
 *
 * This file defines all theme-related types, metadata, and helper functions
 * for the App Store Tracker application.
 */

// Theme names as a union type
export type ThemeName = 'light' | 'dark' | 'terminal';

// Animation intensity levels
export type AnimationLevel = 'none' | 'subtle' | 'dramatic';

// Font family types
export type FontFamily = 'sans' | 'mono';

// Theme effects configuration
export interface ThemeEffects {
  scanlines: boolean;
  glow: boolean;
  crtOverlay: boolean;
  animations: AnimationLevel;
}

// Theme fonts configuration
export interface ThemeFonts {
  display: FontFamily;
  data: FontFamily;
}

// Complete theme configuration
export interface ThemeConfig {
  name: string;
  description: string;
  icon: string;
  effects: ThemeEffects;
  fonts: ThemeFonts;
}

// All available themes
export const themes: Record<ThemeName, ThemeConfig> = {
  light: {
    name: 'Light',
    description: 'Clean and bright',
    icon: 'Sun',
    effects: {
      scanlines: false,
      glow: false,
      crtOverlay: false,
      animations: 'subtle'
    },
    fonts: {
      display: 'sans',
      data: 'sans'
    }
  },
  dark: {
    name: 'Dark',
    description: 'Easy on the eyes',
    icon: 'Moon',
    effects: {
      scanlines: false,
      glow: false,
      crtOverlay: false,
      animations: 'subtle'
    },
    fonts: {
      display: 'sans',
      data: 'sans'
    }
  },
  terminal: {
    name: 'Terminal',
    description: 'Bloomberg terminal style',
    icon: 'Monitor',
    effects: {
      scanlines: true,
      glow: true,
      crtOverlay: true,
      animations: 'dramatic'
    },
    fonts: {
      display: 'mono',
      data: 'mono'
    }
  }
};

/**
 * Get the complete theme configuration for a given theme name
 * @param theme - The theme name
 * @returns The complete ThemeConfig object
 */
export function getThemeConfig(theme: ThemeName): ThemeConfig {
  return themes[theme];
}
